import { useEffect, useState } from "react";
import { getCCTVHealthStatus, getCCTVHealthStatusByGroup } from "../../Api Methods/Api";
import { useDispatch, useSelector } from "react-redux";
import integClasses from './CCTVIntegrations.module.css';
import classes from './CCTVAnalytics.module.css';
import Stats2 from "../../components/UI/Stats/Stats2";

const STATUS_COLOR = {
    Online:    "#5fdc8d",
    Unstable:  "#efbc65",
    Offline:   "#ee6d6d",
    "No Data": "#9ca3af",
};

const classifyType = (successCount, failCount) => {
    const total = successCount + failCount;
    if (total === 0) return { successRate: null, status: "No Data", successCount: 0, failCount: 0 };

    const successRate = Math.round((successCount / total) * 100);
    const isOffline  = successCount === 0;
    const isUnstable = !isOffline && (failCount / total) >= 0.10;

    return {
        successRate,
        status: isOffline ? "Offline" : isUnstable ? "Unstable" : "Online",
        successCount,
        failCount,
    };
};

const HealthCard = ({ title, data }) => {
    const color = STATUS_COLOR[data.status];
    const pct   = data.successRate ?? 0;
    return (
        <div className={classes.healthCard}>
            <div className={classes.healthCardHeader}>
                <span className={classes.healthCardType}>{title}</span>
                <span className={classes.statusBadge} style={{ background: `${color}22`, color }}>
                    {data.status}
                </span>
            </div>
            <div className={classes.percentageWrap}>
                <span className={classes.percentage} style={{ color }}>
                    {data.successRate !== null ? `${pct}%` : "—"}
                </span>
                <span className={classes.percentageLabel}>success rate</span>
            </div>
            <div className={classes.progressBar}>
                <div
                    className={classes.progressFill}
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
            <p className={classes.pollCounts}>
                {data.successCount.toLocaleString()} successful · {data.failCount.toLocaleString()} failed
            </p>
        </div>
    );
};

const SkeletonHealthCard = () => (
    <div className={classes.healthCard}>
        <div className={classes.skeletonRow}>
            <div className={classes.skeleton} style={{ height: 14, width: "40%" }} />
            <div className={classes.skeleton} style={{ height: 22, width: "22%", borderRadius: 12 }} />
        </div>
        <div className={classes.skeleton} style={{ height: 44, width: "55%", margin: "20px auto 6px" }} />
        <div className={classes.skeleton} style={{ height: 12, width: "35%", margin: "0 auto 24px" }} />
        <div className={classes.skeleton} style={{ height: 8, width: "100%", borderRadius: 4 }} />
        <div className={classes.skeleton} style={{ height: 12, width: "58%", margin: "14px auto 0" }} />
    </div>
);

const CHARTS = [
    { title: "AJAX",       keys: ["ajaxSuccessCount",      "ajaxFailCount"]      },
    { title: "Dahua",      keys: ["dahuaSuccessCount",     "dahuaFailCount"]     },
    { title: "Hikvision",  keys: ["hikvisionSuccessCount", "hikvisionFailCount"] },
    { title: "AIBox",      keys: ["aiboxSuccessCount",     "aiboxFailCount"]     },
];

const CCTVAnalyticsGroup = ({groupID}) => {
    const [stats, setStats]       = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const client   = useSelector(state => state.auth.client);
    const token    = useSelector(state => state.auth.token);
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchStats = async () => {
            const response = await getCCTVHealthStatusByGroup({ token, client }, groupID, dispatch);
            setStats(response.data[0][0]);
            setIsLoading(false);
        };
        fetchStats();
    }, []);

    const classified = stats
        ? CHARTS.map(({ title, keys }) => ({
            title,
            data: classifyType(stats[keys[0]], stats[keys[1]]),
          }))
        : null;

    const types        = classified?.map(c => c.data) ?? [];
    const onlineCount  = types.filter(t => t.status === "Online").length;
    const unstableCount = types.filter(t => t.status === "Unstable").length;
    const offlineCount = types.filter(t => t.status === "Offline").length;

    return (
        <div className={classes.analyticsWrap}>
            

            <div className={classes.healthGrid}>
                {isLoading
                    ? [...Array(4)].map((_, i) => <SkeletonHealthCard key={i} />)
                    : classified.map(({ title, data }) => (
                        <HealthCard key={title} title={title} data={data} />
                    ))
                }
            </div>

            <div className={classes.comparisonCard}>
                <p className={classes.comparisonTitle}>Poll success rate by integration type</p>
                <div className={classes.comparisonRows}>
                    {isLoading
                        ? [...Array(4)].map((_, i) => (
                            <div key={i} className={classes.comparisonRow}>
                                <div className={classes.skeleton} style={{ height: 13, width: 72 }} />
                                <div className={classes.skeleton} style={{ height: 20, flex: 1 }} />
                                <div className={classes.skeleton} style={{ height: 13, width: 36 }} />
                            </div>
                          ))
                        : classified.map(({ title, data }) => {
                            const color = STATUS_COLOR[data.status];
                            const pct   = data.successRate ?? 0;
                            return (
                                <div key={title} className={classes.comparisonRow}>
                                    <span className={classes.comparisonLabel}>{title}</span>
                                    <div className={classes.comparisonBar}>
                                        <div
                                            className={classes.comparisonFill}
                                            style={{
                                                width: `${pct}%`,
                                                background: color,
                                                borderRadius: pct === 100 ? 4 : "4px 0 0 4px",
                                            }}
                                        />
                                    </div>
                                    <span className={classes.comparisonPct} style={{ color }}>
                                        {data.successRate !== null ? `${pct}%` : "—"}
                                    </span>
                                </div>
                            );
                          })
                    }
                </div>
            </div>
        </div>
    );
};

export default CCTVAnalyticsGroup;
